import { useEffect, useState, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Extension } from '@tiptap/core';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, ArrowLeft } from 'lucide-react';
import type { Signature } from '@/types/email';
import { cn } from '@/lib/utils';

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
});

const SignatureTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    return ['table', { ...HTMLAttributes, style: 'border-collapse: collapse; width: 100%;' }, ['tbody', 0]];
  },
});

const SignatureTableHeader = TableHeader.extend({
  renderHTML({ HTMLAttributes }) {
    return ['th', { ...HTMLAttributes, style: 'border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;' }, 0];
  },
});

const SignatureTableCell = TableCell.extend({
  renderHTML({ HTMLAttributes }) {
    return ['td', { ...HTMLAttributes, style: 'border: 1px solid #ddd; padding: 8px;' }, 0];
  },
});

interface SignatureFormProps {
  signature?: Signature | null;
  onSave: (data: { name: string; html: string }) => Promise<void>;
  onCancel: () => void;
  saving?: boolean;
}

export function SignatureForm({ signature, onSave, onCancel, saving = false }: SignatureFormProps) {
  const [name, setName] = useState(signature?.name || '');
  const [html, setHtml] = useState(signature?.html || '');
  const [mode, setMode] = useState<'visual' | 'html'>('visual');
  const [error, setError] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
        heading: { levels: [1, 2, 3, 4] },
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      FontSize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: 'text-primary underline decoration-primary/50 underline-offset-4 font-medium',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      SignatureTable.configure({
        resizable: false,
        allowTableNodeSelection: true,
      }),
      TableRow,
      SignatureTableHeader,
      SignatureTableCell,
    ],
    content: html || '<p></p>',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[220px] whitespace-pre-wrap rounded-lg border border-border/60 bg-background px-3 py-2 leading-relaxed text-foreground focus:outline-none',
      },
    },
    onUpdate({ editor: currentEditor }) {
      if (mode === 'visual') {
        setHtml(currentEditor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor && mode === 'visual' && html !== editor.getHTML()) {
      editor.commands.setContent(html);
    }
  }, [mode, editor, html]);

  const visualToolbar = useMemo(() => {
    if (!editor) {
      return null;
    }
    return (
      <div className="flex flex-wrap items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('bold') ? 'bg-background shadow-sm' : '')}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('italic') ? 'bg-background shadow-sm' : '')}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('underline') ? 'bg-background shadow-sm' : '')}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border/60" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('bulletList') ? 'bg-background shadow-sm' : '')}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn('h-8 w-8 p-0', editor.isActive('orderedList') ? 'bg-background shadow-sm' : '')}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border/60" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'left' }) ? 'bg-background shadow-sm' : '')}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'center' }) ? 'bg-background shadow-sm' : '')}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={cn('h-8 w-8 p-0', editor.isActive({ textAlign: 'right' }) ? 'bg-background shadow-sm' : '')}
        >
          <AlignRight className="h-4 w-4" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border/60" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            const previousUrl = editor.getAttributes('link').href;
            const url = window.prompt('URL', previousUrl);
            if (url === null) {
              return;
            }
            if (url === '') {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
          className={cn('h-8 w-8 p-0', editor.isActive('link') ? 'bg-background shadow-sm' : '')}
        >
          <Link2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }, [editor]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    setError(null);
    try {
      await onSave({ name: trimmedName, html });
    } catch (e) {
      setError('Failed to save signature');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-4 border-b border-border/40">
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">{signature ? 'Edit Signature' : 'New Signature'}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          <div className="space-y-2">
            <Label htmlFor="signature-name">Name</Label>
            <Input
              id="signature-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={'Friendly name (e.g. "Default", "Support Team")'}
              disabled={saving}
            />
          </div>

          <Tabs value={mode} onValueChange={(value) => setMode(value as 'visual' | 'html')} className="flex flex-col gap-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="visual">Visual Editor</TabsTrigger>
              <TabsTrigger value="html">HTML</TabsTrigger>
            </TabsList>
            <TabsContent value="visual" className="flex flex-col gap-2 mt-0">
              {visualToolbar}
              <div className="overflow-hidden rounded-lg border border-border/60 bg-background min-h-[220px]">
                <EditorContent editor={editor} className="h-full p-2" />
              </div>
            </TabsContent>
            <TabsContent value="html" className="mt-0">
              <Textarea
                className="min-h-[260px] font-mono text-sm"
                value={html}
                onChange={(event) => setHtml(event.target.value)}
                disabled={saving}
              />
            </TabsContent>
          </Tabs>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : 'Save Signature'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
